/**
 * Helpers for AI audiences added to an existing ad campaign with daily budget moved from one of its ad sets.
 *
 * Wizard state used here:
 *   salesFunnelSetup.audienceBuilder.budgetMove   { donorAdSetId, donorName, donorBudget, totalBudget, minDailyBudget,
 *                                                 currency } (minor units); the amount moved is the builder's
 *                                                 acquisitionBudget
 *   addAudienceBudgetMove.confirmedFor           the move the user confirmed on the review step
 *
 * The whole daily budget of the new ad sets comes from the donor, so the campaign's total daily budget stays the same.
 * Budgets of zero-decimal currencies (JPY and others) are stored ×100 and must be whole amounts.
 */
import { getBuilderState, getHypothesisMap, parseBudgetAmount, toMinorUnits } from '@/utils/audience-builder'
import { isZeroDecimalCurrency } from '@/utils/tools'

/** Minor units per whole amount of the currency: 100 for zero-decimal currencies, 1 otherwise. */
export const wholeUnitOf = (currency) => (currency && isZeroDecimalCurrency(currency) ? 100 : 1)

export const BUDGET_MOVE_STEP = 'addAudienceBudgetMove'

export const LEARNING_STATUS_LABELS = {
    learning: 'Learning',
    completed: 'Learning complete',
    learning_limited: 'Learning limited',
}

export const getLearningStatusLabel = (status) => LEARNING_STATUS_LABELS[status] || 'Learning status not available'

export const BUDGET_MOVE_NOTE =
    'Lowering the budget of an ad set is a significant edit for Meta: that ad set may go back into learning. The ad sets of this campaign are not changed otherwise.'

export const BUDGET_MOVE_SAFETY_NOTE =
    'The new audiences are created paused. They are switched on only in an active campaign, only after the budget was lowered on Meta, and only while that ad set is delivering; in a paused campaign they stay paused. If the budget cannot be lowered, they stay paused and no budget is moved; audiences that cannot be created are removed and their budget stays where it is.'

export const BUDGET_MOVE_ERROR_MESSAGES = {
    ADD_AUDIENCE_NOT_ELIGIBLE:
        'AI audiences cannot be added to this campaign any more. Use the existing audiences instead.',
    BUDGET_MOVE_REQUIRED: 'Choose the ad set the budget of the AI audiences comes from, in Sales Funnel Setup.',
    INVALID_BUDGET_MOVE:
        'The budget move is no longer valid. Check the ad set and amount in Sales Funnel Setup and the budgets of the new audiences.',
    BUDGET_MOVE_NOT_CONFIRMED: 'Confirm the budget move before adding the audiences.',
    BUDGET_MOVE_CHANGED:
        'The budget of the ad set changed since you confirmed the move, for example because these audiences were already added. Check the campaign in Ads Manager, then choose the ad set again in Sales Funnel Setup.',
}

/** Message for a launch or generation error caused by the budget move, or null when the error is something else. */
export const getBudgetMoveErrorMessage = (error) => {
    const code = error?.graphQLErrors?.[0]?.extensions?.code
    return BUDGET_MOVE_ERROR_MESSAGES[code] || null
}

/** The donor and amount chosen for the AI audiences, or null outside AI mode or without a donor and an amount. */
export const getBudgetMove = (salesFunnelData) => {
    const builder = getBuilderState(salesFunnelData)
    const amount = parseBudgetAmount(builder.acquisitionBudget)
    if (builder.mode !== 'ai' || !builder.budgetMove?.donorAdSetId || !amount) return null
    const movedBudget = toMinorUnits(amount)
    return {
        ...builder.budgetMove,
        movedBudget,
        donorBudgetAfter: builder.budgetMove.donorBudget - movedBudget,
    }
}

/** Stores the chosen donor (from the eligibility result) in the Sales Funnel Setup step data. */
export const setBudgetMoveDonor = (data, donor, eligibility) => {
    const builder = getBuilderState(data)
    return {
        ...data,
        audienceBuilder: {
            ...builder,
            budgetMove: donor
                ? {
                      donorAdSetId: donor.ad_set_id,
                      donorName: donor.name,
                      donorBudget: donor.budget,
                      totalBudget: eligibility?.total_budget ?? null,
                      minDailyBudget: eligibility?.min_daily_budget ?? null,
                      currency: eligibility?.currency ?? null,
                  }
                : null,
        },
    }
}

/** Why the amount cannot be moved from the donor, or null. `amount` is in major units, as typed. */
export const budgetMoveAmountProblem = (amount, donor, minDailyBudget, currency = null) => {
    const value = parseBudgetAmount(amount)
    if (!donor) return 'Choose the ad set the budget comes from.'
    if (!value) return 'Enter the daily amount to move.'
    const minor = toMinorUnits(value)
    if (Math.abs(minor - value * 100) > 1e-6) return 'Enter the amount in whole cents.'
    if (minor % wholeUnitOf(currency) !== 0) return `Enter a whole amount: ${currency} budgets have no decimals.`
    if (minDailyBudget && minor < minDailyBudget)
        return 'The amount must be at least the minimum daily budget of an ad set.'
    if (minor > donor.max_movable) return 'The ad set must keep at least the minimum daily budget of your ad account.'
    return null
}

/** Whether AI audiences are selected in the acquisition stage. */
export const hasSelectedAiAudiences = (salesFunnelData) => {
    const map = getHypothesisMap(salesFunnelData)
    return (salesFunnelData?.audienceSelections?.acquisition || []).some((id) => map[id])
}

/** Identifies one move, so a confirmation never covers a different donor, amount or donor budget. */
export const budgetMoveKey = (move) => (move ? `${move.donorAdSetId}:${move.movedBudget}:${move.donorBudget}` : null)

export const isBudgetMoveConfirmed = (move, confirmation) =>
    !!move && confirmation?.confirmedFor === budgetMoveKey(move)

/**
 * The `budget_move` input of the launch, or null when no AI audience is added. The donor budget shown in the
 * confirmation is sent with it, so the server refuses the move once that budget changed (also for a repeated request).
 */
export const budgetMoveForSubmission = (salesFunnelData, confirmation) => {
    const move = getBudgetMove(salesFunnelData)
    if (!move || !hasSelectedAiAudiences(salesFunnelData)) return null
    return {
        donor_ad_set_id: move.donorAdSetId,
        moved_budget: move.movedBudget,
        confirmed: isBudgetMoveConfirmed(move, confirmation),
        donor_budget_before: move.donorBudget,
    }
}

/**
 * Splits an amount (minor units) over audience ids in steps of `unit` minor units (100 for zero-decimal currencies,
 * so every share is a whole amount); the first ones get the remainder.
 */
export const splitBudget = (amount, ids, unit = 1) => {
    if (!ids.length) return {}
    const units = Math.floor(amount / unit)
    const base = Math.floor(units / ids.length)
    const remainder = units - base * ids.length
    const shares = ids.map((id, index) => [id, (base + (index < remainder ? 1 : 0)) * unit])
    // An amount that is not a whole number of units keeps its rest on the first share, so the sum stays exact
    shares[0][1] += amount - units * unit
    return Object.fromEntries(shares)
}

/** Whether every manual budget of the given audiences is a whole amount of the currency. */
export const manualBudgetsAreWhole = (manualBudgets, audiencesByStage, currency) => {
    const unit = wholeUnitOf(currency)
    return Object.entries(audiencesByStage || {}).every(([stage, ids]) =>
        (ids || []).every(
            (id) =>
                Math.round((Number.parseFloat(manualBudgets?.[stage]?.audiences?.[id]?.amount) || 0) * 100) % unit ===
                0,
        ),
    )
}

/** Daily budget (minor units) of the given audiences in the Budget step's manual budgets. */
export const manualBudgetTotal = (manualBudgets, audiencesByStage) =>
    Object.entries(audiencesByStage || {}).reduce(
        (sum, [stage, ids]) =>
            sum +
            (ids || []).reduce(
                (stageSum, id) =>
                    stageSum +
                    Math.round((Number.parseFloat(manualBudgets?.[stage]?.audiences?.[id]?.amount) || 0) * 100),
                0,
            ),
        0,
    )

/** Plain lines describing the move: donor before and after, the new ad sets and the unchanged campaign total. */
export const describeBudgetMove = (move, format) => {
    if (!move) return []
    const lines = [
        `${format(move.movedBudget)} a day moves from "${move.donorName}" to the new ad sets.`,
        `"${move.donorName}": ${format(move.donorBudget)} a day now, ${format(move.donorBudgetAfter)} a day after the move.`,
    ]
    if (move.totalBudget !== null && move.totalBudget !== undefined) {
        lines.push(`Total daily budget of the campaign: ${format(move.totalBudget)} before and after the move.`)
    }
    return lines
}

/** Generation inputs with the chosen donor, so a set built for another donor counts as outdated. */
export const withDonorInput = (inputs, salesFunnelData) => {
    const donorAdSetId = getBuilderState(salesFunnelData).budgetMove?.donorAdSetId
    return donorAdSetId ? { ...inputs, donorAdSetId } : inputs
}
